import React from "react";

import { createTheme } from "@material-ui/core";
import {
  alpha,
  darken,
  lighten,
} from "@material-ui/core/styles/colorManipulator";
import { ThemeProvider as MuiThemeProvider } from "@material-ui/styles";

const iglu = {
  ceruleanBlue: "#00afee",
  cobaltBlue: "#0a73e0",
  denimBlue: "#021033",
  denimBlue95: alpha("#021033", 0.95),
  yellowSunshine: "#fdd907",
  darkBlue1: "#001a70",
  darkBlue2: "#0c3df7",
};

const common = {
  background: "#f5f5f5",
  black: "#000",
  grey: "#e0e0e0",
  greyDark: "#747474",
  white: "#fff",
  white05: alpha("#fff", 0.05),
  white10: alpha("#fff", 0.1),
  white50: alpha("#fff", 0.5),
};

const colors = {
  primary: createColor(iglu.cobaltBlue, common.white),
  secondary: createColor(common.greyDark, common.white),
  success: createColor("#18ad36", common.white),
  info: createColor(iglu.ceruleanBlue, common.white),
  warning: createColor(iglu.yellowSunshine, common.black),
  danger: createColor("#df2935", common.white),
};

function createColor(color, contrastText) {
  return {
    contrastText,
    dark: darken(color, 0.2),
    darkest: darken(color, 0.6),
    light: lighten(color, 0.2),
    lightest: lighten(color, 0.8),
    main: color,
  };
}

const options = {
  /**
   * @see https://material-ui.com/customization/palette/
   */
  palette: {
    ...colors,
    common,
    contrastThreshold: 2,
    iglu,
  },

  /**
   * @see https://material-ui.com/customization/default-theme/?expand-path=$.breakpoints.values
   */
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1440,
      xl: 1920,
    },
  },

  /**
   * @see https://material-ui.com/customization/themes/#theme-configuration-variables
   */
  typography: {
    fontFamily: "Poppins, Arial, sans-serif",
    htmlFontSize: 16,

    h1: {
      color: iglu.denimBlue,
      fontSize: 48,
      fontWeight: 700,
    },
    h2: {
      color: iglu.denimBlue,
      fontSize: 16,
      lineHeight: 1.25,
    },
    h3: {
      color: iglu.denimBlue,
      fontSize: 14,
    },
    subtitle1: {
      color: iglu.denimBlue,
      fontSize: 24,
      marginTop: 8,
      lineHeight: 1.25,
    },
    subtitle2: {
      color: iglu.denimBlue,
      fontSize: 12,
      marginTop: 4,
      lineHeight: 1.25,
    },
    body1: {
      fontSize: 14,
    },
    body2: {
      fontSize: 12,
    },
    monospace: {
      fontFamily: "B612 Mono, monospace",
      letterSpacing: -0.5,
    },
  },

  /**
   * @see https://material-ui.com/customization/globals/#css
   */
  overrides: {
    MuiAlert: {
      action: {
        alignItems: "flex-start",
      },
      icon: {
        padding: "2px 0",
      },
      message: {
        padding: "3px 0",
      },
      root: {
        paddingLeft: 10,
        "&:not(:last-child)": {
          marginBottom: 8,
        },
      },
    },
    MuiAlertTitle: {
      root: {
        marginBottom: 0,
      },
    },
    MuiButton: {
      root: {
        minWidth: 0,
        fontSize: 13,
        padding: "5px 10px",
        textTransform: "none",
        whiteSpace: "nowrap",
        "&.disabled": {
          cursor: "default !important",
          opacity: 0.5,
        },
        "&.icon": {
          fontSize: 16,
          padding: 7,
        },
        "& .MuiCircularProgress-root": {
          marginLeft: "0.5rem",
          color: "inherit",
          padding: 0,
          "&:first-child": {
            marginLeft: 0,
          },
        },
      },
      outlined: {
        padding: "4px 9px",
      },
      outlinedSizeSmall: {
        "&.icon.icon": {
          padding: 3,
        },
      },
      sizeSmall: {
        "&.icon": {
          padding: 4,
        },
      },
    },
    MuiChip: {
      root: {
        fontSize: "0.8125em",
        height: "1.5em",
      },
      label: {
        paddingLeft: "0.5em",
        paddingRight: "0.5em",
        letterSpacing: 0.5,
      },
    },
    MuiCard: {
      root: {
        pageBreakBefore: "always",
        "&:not(:last-child)": {
          marginBottom: 24,
        },
      },
    },
    MuiCardContent: {
      root: {
        borderTop: `1px solid ${common.grey}`,
        "&:last-child": {
          paddingBottom: 16,
        },
      },
    },
    MuiCheckbox: {
      root: {
        "&$checked": {
          color: `${colors.primary.main} !important`,
        },
      },
    },
    MuiDialog: {
      container: {
        background: "rgba(0,0,0,0.3)",
      },
    },
    MuiDialogActions: {
      root: {
        padding: "16px 24px",
      },
    },
    MuiDialogContent: {
      dividers: {
        padding: 24,
        "& ~ &": {
          borderTop: 0,
        },
      },
      root: {
        "&, &:first-child": {
          padding: 24,
        },
      },
    },
    MuiDrawer: {
      paper: {
        minWidth: 200,
      },
    },
    MuiFormLabel: {
      root: {
        fontSize: 12,
      },
    },
    MuiInputAdornment: {
      root: {
        "& .MuiIconButton-root": {
          padding: 4,
        },
      },
    },
    MuiInputBase: {
      root: {
        fontFamily: "B612 Mono, monospace",
        letterSpacing: -0.5,
        fontSize: 12,
      },
    },
    MuiOutlinedInput: {
      adornedEnd: {
        paddingRight: 4,
      },
    },
    MuiListItemIcon: {
      root: {
        minWidth: 40,
      },
    },
    MuiMenuItem: {
      root: {
        padding: "10px 20px",
      },
    },
    MuiPickersMonth: {
      root: {
        fontSize: 20,
        marginTop: 0,
      },
    },
    MuiSelect: {
      root: {
        "&.MuiInputBase-inputAdornedEnd, &.MuiInputBase-inputAdornedEnd.MuiOutlinedInput-inputAdornedEnd":
          {
            paddingRight: 16,
            "& ~ .MuiSelect-icon": {
              right: 32,
            },
          },
      },
    },
    MuiSvgIcon: {
      fontSizeLarge: {
        margin: 5,
      },
    },
    MuiTableCell: {
      root: {
        padding: 8,
        lineHeight: 1.75,
        "&.monospace, & .monospace": {
          fontFamily: "B612 Mono, monospace",
          letterSpacing: -0.5,
        },
        "&:first-child": {
          paddingLeft: 12,
        },
        "&:last-child": {
          paddingRight: 12,
        },
      },
      sizeSmall: {
        padding: 3,
      },
    },
    MuiTooltip: {
      tooltip: {
        backgroundColor: common.white,
        border: `1px solid ${common.grey}`,
        color: common.black,
        fontSize: "1em",
        maxWidth: "none",
        textAlign: "center",
        whiteSpace: "pre-line",
      },
      arrow: {
        color: common.grey,
        "&:before": {
          position: "relative",
          top: "1px",
        },
        "&:after": {
          content: '""',
          display: "block",
          backgroundColor: common.white,
          position: "relative",
          width: "1em",
          height: "1em",
          margin: "auto",
          transform: "rotate(45deg)",
          top: "calc(-1em - 5px)",
        },
      },
    },
    Pager: {
      pager: {
        padding: "8px 0 0",
      },
    },
    TableCell: {
      cell: {
        "&:first-child": {
          paddingLeft: 12,
        },
        "&:last-child": {
          paddingRight: 12,
        },
      },
    },
    TableDetailToggleCell: {
      toggleCell: {
        paddingLeft: "8px !important",
      },
      toggleCellButton: {
        padding: 4,
      },
    },
    TableHeaderCell: {
      cell: {
        "&:first-child": {
          paddingLeft: 12,
        },
        "&:last-child": {
          paddingRight: 12,
        },
      },
    },
    TableSelectAllCell: {
      cell: {
        paddingLeft: "0 !important",
      },
      checkbox: {
        padding: 4,
      },
    },
    TableSelectCell: {
      cell: {
        paddingLeft: "0 !important",
      },
      checkbox: {
        padding: 4,
      },
    },
  },

  /**
   * @see https://material-ui.com/customization/globals/#default-props
   */
  props: {
    MuiAppBar: {
      elevation: 0,
    },
    MuiButton: {
      disableElevation: true,
    },
    MuiButtonBase: {
      disableRipple: true,
    },
    MuiCard: {
      elevation: 0,
    },
    MuiFormControl: {
      fullWidth: true,
      size: "small",
    },
    MuiTextField: {
      fullWidth: true,
    },
    MuiTooltip: {
      arrow: true,
      placement: "top",
    },
  },
};

/*
 * Custom colors
 */
Object.entries(colors).forEach(([name, color]) => {
  options.overrides.MuiButton.root[`&.${name}`] = {
    "&, &.disabled:hover": {
      backgroundColor: color.main,
      color: color.contrastText,
    },
    "&:hover": {
      backgroundColor: color.dark,
    },
  };
  options.overrides.MuiButton.outlined[`&.${name}`] = {
    "&, &.disabled:hover": {
      backgroundColor: "transparent",
      borderColor: color.main,
      color: color.main,
    },
    "&:hover": {
      backgroundColor: color.main,
      color: color.contrastText,
    },
  };
  options.overrides.MuiChip.root[`&.${name}`] = {
    backgroundColor: color.main,
    color: color.contrastText,
  };
});

const theme = createTheme(options);

export default function ThemeProvider({ children }) {
  return <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>;
}
